import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CalendarCheck, CheckCircle2, LogOut, MapPin, PackageCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { formatDate, formatDateTime, formatNumber } from "../lib/format";

type Citizen = {
  household_code: string;
  head_name: string;
  status: string;
  phone_last4: string | null;
  last_delivered_at: string | null;
  delivery_count: number;
  foundational_rank: number | null;
  unit_name: string;
  allocation_id: string | null;
  allocation_status: string | null;
  batch_number: string | null;
  scheduled_date: string | null;
  agent_name: string | null;
};
type Delivery = {
  id: string;
  delivered_at: string;
  status: string;
  batch_number: string;
  agent_name: string;
};
type Notification = {
  id: string;
  title_ar: string;
  body_ar: string;
  status: string;
  sent_at: string;
  read_at: string | null;
};
export function CitizenPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useQuery({
    queryKey: ["citizen-profile"],
    queryFn: async () => (await api<Citizen>("/citizen/me")).data
  });
  const deliveries = useQuery({
    queryKey: ["citizen-deliveries"],
    queryFn: async () => (await api<Delivery[]>("/citizen/me/deliveries")).data
  });
  const notifications = useQuery({
    queryKey: ["citizen-notifications"],
    queryFn: async () => (await api<Notification[]>("/citizen/me/notifications")).data
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api(`/citizen/me/notifications/${id}/read`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["citizen-notifications"] })
  });
  async function exit() {
    await logout();
    void navigate("/login", { replace: true });
  }
  if (profile.isPending)
    return (
      <main className="center-screen">
        <LoadingState />
      </main>
    );
  if (profile.isError)
    return (
      <main className="center-screen">
        <ErrorState error={profile.error} />
      </main>
    );
  const citizen = profile.data;
  return (
    <div className="citizen-page">
      <header className="citizen-header">
        <div className="brand">
          <span className="brand-mark">ت</span>
          <span>
            <strong>تموين</strong>
            <small>بوابة المواطن</small>
          </span>
        </div>
        <button className="button button--ghost-light" type="button" onClick={exit}>
          <LogOut aria-hidden="true" />
          خروج
        </button>
      </header>
      <main className="citizen-main">
        <section className="citizen-welcome">
          <p>أهلًا بك</p>
          <h1>{citizen.head_name}</h1>
          <span>{citizen.household_code}</span>
        </section>
        {citizen.allocation_id ? (
          <section className="eligibility-card eligibility-card--ready">
            <div className="eligibility-icon">
              <CheckCircle2 aria-hidden="true" />
            </div>
            <div>
              <span>لديك استحقاق حالي</span>
              <h2>تم تخصيص أسطوانة لأسرتك</h2>
              <p>
                الدفعة {citizen.batch_number} لدى {citizen.agent_name}، الموعد{" "}
                {formatDate(citizen.scheduled_date)}.
              </p>
            </div>
            <StatusBadge status={citizen.allocation_status ?? "allocated"} />
          </section>
        ) : (
          <section className="eligibility-card">
            <div className="eligibility-icon">
              <CalendarCheck aria-hidden="true" />
            </div>
            <div>
              <span>حالة الدور</span>
              <h2>لا يوجد تخصيص نشط حاليًا</h2>
              <p>
                ترتيبك التأسيسي:{" "}
                {citizen.foundational_rank ? formatNumber(citizen.foundational_rank) : "قيد الإعداد"}. ستصلك
                رسالة داخلية عند التخصيص.
              </p>
            </div>
          </section>
        )}
        <section className="citizen-grid">
          <article className="content-card">
            <h2>بيانات الأسرة</h2>
            <dl className="definition-grid">
              <div>
                <dt>
                  <MapPin aria-hidden="true" />
                  الوحدة
                </dt>
                <dd>{citizen.unit_name}</dd>
              </div>
              <div>
                <dt>
                  <PackageCheck aria-hidden="true" />
                  آخر استلام
                </dt>
                <dd>{formatDate(citizen.last_delivered_at)}</dd>
              </div>
              <div>
                <dt>مرات الاستلام</dt>
                <dd>{formatNumber(citizen.delivery_count)}</dd>
              </div>
            </dl>
          </article>
          <article className="content-card">
            <h2>
              <Bell aria-hidden="true" />
              الإشعارات
            </h2>
            {notifications.isPending ? (
              <LoadingState />
            ) : notifications.isError ? (
              <ErrorState error={notifications.error} />
            ) : notifications.data.length === 0 ? (
              <EmptyState title="لا توجد إشعارات" description="ستظهر تنبيهات التخصيص والتسليم هنا." />
            ) : (
              <div className="notification-list">
                {notifications.data.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`notification ${item.read_at ? "notification--read" : ""}`}
                    onClick={() => !item.read_at && markRead.mutate(item.id)}
                  >
                    <strong>{item.title_ar}</strong>
                    <p>{item.body_ar}</p>
                    <time>{formatDateTime(item.sent_at)}</time>
                  </button>
                ))}
              </div>
            )}
          </article>
        </section>
        <section className="content-card">
          <h2>سجل الاستلام</h2>
          {deliveries.isPending ? (
            <LoadingState />
          ) : deliveries.isError ? (
            <ErrorState error={deliveries.error} />
          ) : deliveries.data.length === 0 ? (
            <EmptyState
              title="لا يوجد استلام مسجل"
              description="سيظهر سجل الاستلام هنا بعد إتمام أول عملية."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الدفعة</th>
                    <th>الوكيل</th>
                    <th>التاريخ</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.data.map((item) => (
                    <tr key={item.id}>
                      <td>{item.batch_number}</td>
                      <td>{item.agent_name}</td>
                      <td>{formatDateTime(item.delivered_at)}</td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
